import {
  buildCancellationURLParams,
  isClientOnALaCartePlan,
  isDowngradedMembership,
  isFrictionlessSignupUser,
  type MarketedMembershipPlan,
  type MarketedMembershipPlanType,
} from '@frontend/api-client';
import type { Card } from '@frontend/api-types';
import { makeCent, makeDateISOString } from '@frontend/duck-tape';
import { MEMBER_CANCEL_DOWNGRADE_COPY_DEFAULT_VALUE, useFeatureValue, useSetTimeout } from '@frontend/react';
import { buildDisplayableMembership, type SubscriptionPlan } from '@frontend/react-domain';
import type { PlanManagementState } from '@frontend/react-domain/subscriptions';
import { planManagementStateMachine, PlanManagementStep } from '@frontend/react-domain/subscriptions';
import { Button, Icon, Modal, SkeletonList, Text, XStack, YStack } from '@frontend/web-react';
import { SubscriptionList } from '@frontend/web/components/DomainSpecific/Account/PlanManagement/SubscriptionList';
import { CreditPaymentLoading } from '@frontend/web/components/DomainSpecific/Payment/BuyCreditsModal/CreditPaymentLoading';
import { SelectPayment } from '@frontend/web/components/DomainSpecific/Payment/BuyCreditsModal/SelectPayment';
import {
  getValidatedPromoInput,
  useMembershipPlans,
  useRetrieveClientQuery,
  useUpdatePlanMutation,
} from '@frontend/web/hooks';
import { Env, handleMutation } from '@frontend/web/utils';
import { Divider } from '@mantine/core';
import { useCallback, useEffect, useMemo, useReducer } from 'react';
import { match } from 'ts-pattern';

export type PlanManagementModalProps = {
  closeModal: () => void;
  initialState?: PlanManagementState;
  isOpen: boolean;
  onPlanChange?: () => void;
  stepTitleLookupOverrides?: Partial<Record<PlanManagementStep, (state: PlanManagementState) => string>>;
};

export const PlanManagementModal = ({
  closeModal,
  initialState = { step: PlanManagementStep.selectSubscription },
  isOpen,
  onPlanChange,
  stepTitleLookupOverrides,
}: PlanManagementModalProps) => {
  const {
    value: { modal_body, modal_confirm_button_label, modal_title },
  } = useFeatureValue('member_cancel_downgrade_copy', MEMBER_CANCEL_DOWNGRADE_COPY_DEFAULT_VALUE);
  const titleLookup = { ...DEFAULT_STEP_TITLE_LOOKUP, ...stepTitleLookupOverrides };
  const [{ canGoBack, card, currentSubscriptionPlan, selectedSubscriptionPlan, step }, dispatch] = useReducer(
    planManagementStateMachine,
    initialState,
  );

  // Sync reducer state with latest initialState when modal opens
  // Only depend on isOpen - we want to reset once when opening, not when initialState changes mid-flow
  useEffect(() => {
    if (isOpen) {
      dispatch({ currentSubscriptionPlan: initialState.currentSubscriptionPlan, type: 'reset' });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen]);

  const { data: client, refetch } = useRetrieveClientQuery();
  const currentPlanDetails = useMemo(() => buildDisplayableMembership(client), [client]);
  const [updatePlanMutation, { isLoading: isBuying }] = useUpdatePlanMutation();
  const validatedPromo = getValidatedPromoInput({
    promoCode: selectedSubscriptionPlan?.promotion?.promoCode,
    promotionId: selectedSubscriptionPlan?.promotion?.promotionDetails.promotionId,
  });
  const {
    displayPlanLookup,
    isError: isPlansError,
    isLoading: isPlansLoading,
    refetch: refetchPlans,
  } = useMembershipPlans({ validatedPromo });
  const selectedPlanData = selectedSubscriptionPlan
    ? (displayPlanLookup[selectedSubscriptionPlan.planType] as MarketedMembershipPlan | undefined)
    : undefined;

  const handleContinueWithPlan = useCallback(
    (planType: MarketedMembershipPlanType, promotion?: SubscriptionPlan['promotion']) => {
      dispatch({
        currentSubscriptionPlan,
        selectedSubscriptionPlan: {
          planType,
          promotion,
        },
        type: 'continueWithSelectSubscription',
      });
    },
    [dispatch, currentSubscriptionPlan],
  );

  const handleSelectCard = async (card: Card) => {
    if (!selectedSubscriptionPlan || isBuying) {
      return;
    }

    const planData = displayPlanLookup[selectedSubscriptionPlan.planType];
    if (!planData) return;
    dispatch({ card, currentSubscriptionPlan, selectedSubscriptionPlan, type: 'pay' });
    await handleMutation({
      mutation: () =>
        updatePlanMutation({
          plan: planData.planCode,
          promotionCode: selectedSubscriptionPlan.promotion?.promoCode,
        }).unwrap(),
      onError: () => {
        dispatch({ card, currentSubscriptionPlan, selectedSubscriptionPlan, type: 'failPayment' });
      },
      onSuccess: async () => {
        dispatch({ card, currentSubscriptionPlan, selectedSubscriptionPlan, type: 'completePayment' });
        await refetch();
        onPlanChange?.();
      },
      showSuccessToast: false,
    });
  };

  const handleCancel = () => {
    if (!currentSubscriptionPlan || !client) {
      return;
    }

    const cancellationFormId = Env.VITE_CANCELLATION_FORM_ID.trim();
    if (!cancellationFormId) {
      return;
    }

    const url = `https://form.typeform.com/to/${cancellationFormId}#${buildCancellationURLParams(client)}`;
    window.open(url, '_blank');
    closeAndReset();
  };

  const resetFormAfterDelay = useSetTimeout(
    () => {
      dispatch({ currentSubscriptionPlan, type: 'reset' });
    },
    300,
    { autoStart: false },
  );

  const closeAndReset = () => {
    closeModal();
    resetFormAfterDelay();
  };

  const isDowngrade = useMemo(
    () =>
      Boolean(
        currentSubscriptionPlan &&
          selectedSubscriptionPlan &&
          isDowngradedMembership(currentSubscriptionPlan.planType, selectedSubscriptionPlan.planType),
      ),
    [currentSubscriptionPlan, selectedSubscriptionPlan],
  );

  const initialSubscriptionSelection = useMemo(
    () => selectedSubscriptionPlan?.planType ?? currentSubscriptionPlan?.planType,
    [currentSubscriptionPlan, selectedSubscriptionPlan],
  );

  const canCancel: boolean =
    Boolean(currentSubscriptionPlan) &&
    !isClientOnALaCartePlan(client) &&
    !isFrictionlessSignupUser(client) &&
    !(currentPlanDetails?.pendingChange && currentPlanDetails?.nextMembershipPlan?.isCancelled);

  return (
    <Modal
      Header={
        titleLookup[step] ? (
          <YStack>
            <XStack alignItems="center" justifyContent="between" p="md">
              <XStack alignItems="center" className="size-xl" justifyContent="start">
                {canGoBack ? (
                  <Icon
                    aria-label="Go back"
                    canFocus
                    enableBackgroundColor={false}
                    name="IconArrowLeft"
                    onClick={() => dispatch({ currentSubscriptionPlan, selectedSubscriptionPlan, type: 'back' })}
                    size={24}
                  />
                ) : null}
              </XStack>

              <Text color="secondary" type="h5">
                {titleLookup[step]?.({ canGoBack, card, currentSubscriptionPlan, selectedSubscriptionPlan, step })}
              </Text>

              <XStack alignItems="center" className="size-xl" justifyContent="end">
                <Icon
                  aria-label="Close modal"
                  canFocus
                  enableBackgroundColor={false}
                  name="IconX"
                  onClick={closeAndReset}
                  size={24}
                />
              </XStack>
            </XStack>
            <Divider />
          </YStack>
        ) : null
      }
      closeOnClickOutside={false}
      closeOnEscape={step !== 'processing'}
      isOpen={isOpen}
      onClose={() => {
        if (step !== 'processing') {
          closeAndReset();
        }
      }}
      padding="none"
      size={'xl'}
    >
      <YStack px="xl" py="xl">
        {match(step)
          .with('selectCard', () =>
            selectedSubscriptionPlan ? (
              <SelectPayment
                onSelectCard={handleSelectCard}
                paymentSchedule={
                  isDowngrade
                    ? {
                        amountDueToday: makeCent(0),
                        dueDate: client?.subscriptionEnd ? makeDateISOString(client?.subscriptionEnd) : undefined,
                      }
                    : undefined
                }
                promoCode={selectedSubscriptionPlan?.promotion?.promoCode}
                promotionDescription={selectedSubscriptionPlan?.promotion?.promotionDescription}
                purchasePackage={{
                  planType: selectedSubscriptionPlan.planType as MarketedMembershipPlanType,
                  type: 'subscription',
                }}
                selectedPlanData={selectedPlanData}
              />
            ) : (
              <YStack alignItems="center" gap="sm" justifyContent="center" px="xl" py="xl">
                <YStack bg="red200" p="sm" radius="full">
                  <Icon color="danger" name="IconAlertCircle" size="xl" />
                </YStack>
                <Text type="h5">Error</Text>
                <Text type="p-sm">Please select a subscription - please try again.</Text>
                <Button onClick={() => dispatch({ currentSubscriptionPlan, selectedSubscriptionPlan, type: 'back' })}>
                  Go back
                </Button>
              </YStack>
            ),
          )
          .with('processing', () => (
            <YStack alignItems="center" gap="sm" justifyContent="center" px="xl" py="xl">
              <CreditPaymentLoading />
              {selectedSubscriptionPlan ? (
                <YStack>
                  <Text type="h5">{`We're processing your payment`}</Text>
                  <Text type="p-sm">{`Don't close this window.`}</Text>
                </YStack>
              ) : (
                <YStack>
                  <Text type="h5">{`We're updating your account`}</Text>
                  <Text type="p-sm">{`Don't close this window.`}</Text>
                </YStack>
              )}
            </YStack>
          ))
          .with('success', () => (
            <YStack alignItems="center" gap="sm" justifyContent="center" px="xl" py="xl">
              <YStack bg="green200" p="sm" radius="full">
                <Icon className="hover:cursor-default" color="green" name="IconCheck" size="xl" />
              </YStack>
              {currentPlanDetails?.pendingChange ? (
                <YStack alignItems="center">
                  <Text type="h5">Subscription updated</Text>
                  <YStack gap="sm" py="md">
                    <Divider />
                    <Text color="primary" type="p-sm">
                      Upcoming plan change: {currentPlanDetails.nextMembershipPlan?.membershipPlanName}
                    </Text>
                    <Text color="primary" type="p-sm">
                      Effective on: {currentPlanDetails.nextBillingDate}
                    </Text>
                  </YStack>
                </YStack>
              ) : (
                <YStack alignItems="center">
                  {selectedSubscriptionPlan ? (
                    <>
                      <Text type="h5">Payment successful!</Text>
                      <Text type="p-sm">
                        You successfully upgraded to{' '}
                        {displayPlanLookup[selectedSubscriptionPlan.planType]?.label ?? 'your new plan'}
                      </Text>
                    </>
                  ) : (
                    <Text type="h5">Subscription cancelled</Text>
                  )}
                </YStack>
              )}

              <Button onClick={closeAndReset}>Close</Button>
            </YStack>
          ))
          .with('failedPayment', () => (
            <YStack alignItems="center" gap="sm" justifyContent="center" px="xl" py="xl">
              <YStack bg="red200" p="sm" radius="full">
                <Icon color="danger" name="IconAlertCircle" size="xl" />
              </YStack>
              <Text type="h5">Payment unsuccessful</Text>
              <Text type="p-sm">There was an unexpected error - please try again.</Text>
              <Button onClick={() => dispatch({ currentSubscriptionPlan, selectedSubscriptionPlan, type: 'back' })}>
                Go back
              </Button>
            </YStack>
          ))
          .with('cancel', () => (
            <YStack alignItems="center" justifyContent="center" px="xl" py="xl">
              <YStack className="max-w-sm" gap="sm">
                <Text type="h2">{modal_title}</Text>
                <Text type="p-sm">{modal_body}</Text>
                <YStack pt="lg">
                  <XStack gap="md">
                    <Button onClick={closeAndReset} variant="secondary">
                      cancel
                    </Button>
                    <Button onClick={handleCancel} variant="destructive">
                      {modal_confirm_button_label}
                    </Button>
                  </XStack>
                </YStack>
              </YStack>
            </YStack>
          ))
          .otherwise(() =>
            isPlansLoading ? (
              <SkeletonList countSkeletons={3} />
            ) : isPlansError ? (
              <YStack alignItems="center" gap="sm" justifyContent="center" px="xl" py="xl">
                <YStack bg="red200" p="sm" radius="full">
                  <Icon color="danger" name="IconAlertCircle" size="xl" />
                </YStack>
                <Text type="h5">Your plans took a duck dive.</Text>
                <Text color="secondary" type="p-sm">
                  {"Don't worry, we'll bring them back. Try again."}
                </Text>
                <Button label="Try again" onClick={() => refetchPlans()} size="sm" variant="primary" />
              </YStack>
            ) : (
              <SubscriptionList
                canCancel={canCancel}
                currentMembershipPlanType={currentSubscriptionPlan?.planType}
                initialSelectedPlanType={initialSubscriptionSelection}
                onContinue={handleContinueWithPlan}
                onPlanCancel={
                  currentSubscriptionPlan
                    ? () => {
                        dispatch({ currentSubscriptionPlan, type: 'cancelSubscription' });
                      }
                    : undefined
                }
              />
            ),
          )}
      </YStack>
    </Modal>
  );
};

const DEFAULT_STEP_TITLE_LOOKUP: Partial<Record<PlanManagementStep, (state: PlanManagementState) => string>> = {
  cancel: () => 'Cancel membership?',
  selectCard: () => 'Update membership',
  selectSubscription: (state) => (state.currentSubscriptionPlan ? 'Update membership' : 'Select Subscription'),
};
